/**
 * @iptbc/session-rounding
 * =======================
 * Pure rounding utilities for IPT sign-in / sign-out times.
 *
 * Rounding rules (confirmed with FOP):
 *   Sign-in : (HH-1):45 .. HH:44 → HH:00 (minute >= 45 rolls up)
 *   Sign-out: ANY minute or second past an hour rolls UP to the next
 *             hour. I.e. HH:00:00 stays HH; HH:00:01..(HH+1):00 → HH+1.
 *             Rationale: slots are billed as occupied the moment they
 *             are entered — touching slot N means slot N counts.
 *
 * Timezone:
 *   The functions accept an optional IANA timezone (e.g. 'Asia/Hong_Kong',
 *   'Europe/London', 'America/New_York'). Default is Asia/Hong_Kong to keep
 *   existing call sites + tests working unchanged. The "universal app
 *   timezone" stored in the DB `settings` table is read by the server at
 *   request time and passed in.
 *
 * Uses Intl.DateTimeFormat for tz-aware local-field extraction. Works in
 * Node 18+ and modern browsers (no external dependency).
 */

export const DEFAULT_TZ = 'Asia/Hong_Kong';

function _parts(date, tz) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    throw new TypeError('toLocalParts: expected a valid Date');
  }
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric', month: 'numeric', day: 'numeric',
    hour: 'numeric', minute: 'numeric', second: 'numeric',
    hour12: false,
  });
  const out = {};
  for (const p of fmt.formatToParts(date)) {
    if (p.type === 'literal') continue;
    out[p.type] = parseInt(p.value, 10);
  }
  // Intl returns hour=24 for midnight on some locales / engines. Normalise.
  if (out.hour === 24) out.hour = 0;
  return {
    year: out.year,
    month: out.month,
    day: out.day,
    hour: out.hour,
    minute: out.minute,
    second: out.second,
  };
}

export function toLocalParts(date, tz = DEFAULT_TZ) {
  return _parts(date, tz);
}

// Convert local (tz) field values back to a UTC instant.
// Handles DST/offset changes by sampling the actual offset for that moment.
export function fromLocalParts({ year, month, day, hour, minute = 0, second = 0 }, tz = DEFAULT_TZ) {
  // First-pass guess using a naive UTC of the same fields.
  const naiveUtcMs = Date.UTC(year, month - 1, day, hour, minute, second);
  const offset1 = offsetMinutesAt(new Date(naiveUtcMs), tz);
  const guess = naiveUtcMs - offset1 * 60_000;
  // Second pass — the tz offset at `guess` may differ slightly (DST edge).
  const offset2 = offsetMinutesAt(new Date(guess), tz);
  return new Date(naiveUtcMs - offset2 * 60_000);
}

function offsetMinutesAt(date, tz) {
  const p = _parts(date, tz);
  const localMs = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return Math.round((localMs - date.getTime()) / 60_000);
}

function rolledOver(year, month, day, hour, tz) {
  const boundary = fromLocalParts({ year, month, day, hour }, tz);
  const parts = _parts(boundary, tz);
  return { date: boundary, hour: parts.hour, parts };
}

export function roundSignIn(date, tz = DEFAULT_TZ) {
  const p = _parts(date, tz);
  let hour = p.hour;
  if (p.minute >= 45) hour += 1;
  return rolledOver(p.year, p.month, p.day, hour, tz);
}

export function roundSignOut(date, tz = DEFAULT_TZ) {
  const p = _parts(date, tz);
  let hour = p.hour;
  // Any positive minute OR second past the hour means the pilot has
  // entered slot N (where N = current hour), and therefore "used" it.
  // Only HH:00:00 exact stays on hour HH.
  if (p.minute > 0 || p.second > 0) hour += 1;
  return rolledOver(p.year, p.month, p.day, hour, tz);
}

export function hoursBetween(signInRounded, signOutRounded) {
  const ms = signOutRounded.date.getTime() - signInRounded.date.getTime();
  if (ms <= 0) return 0;
  return Math.round(ms / 3_600_000);
}

// Legacy aliases — kept for backwards compatibility with the existing test
// suite (and any other call sites that haven't been migrated to the
// timezone-aware names yet).
export const HK_TZ = 'Asia/Hong_Kong';
export const HK_OFFSET_MINUTES = 8 * 60;
export const toHkParts = toLocalParts;
export const fromHkParts = fromLocalParts;
